import { useCallback } from "react";
import {
  scrollToFirstInvalidField,
  scrollToValidationError
} from "../utilities/scrollUtilities";

/**
 * Options for useFormValidation hook.
 * @property scrollOffset - Offset from the top in pixels for scrolling (default:180)
 * @property scrollBehavior - Scroll behavior (default: 'smooth')
 * @property scrollDelay - Delay in milliseconds before scrolling (default:200)
 */
export interface UseFormValidationOptions {
  scrollOffset?: number;
  scrollBehavior?: ScrollBehavior;
  scrollDelay?: number;
}

/**
 * Return type for useFormValidation hook.
 */
export interface UseFormValidationReturn {
  handleValidationError: (
    formSelector?: string,
    errorSelector?: string,
    skipScroll?: boolean
  ) => Promise<void>;
  scrollToValidationError: (errorSelector?: string) => Promise<void>;
  scrollToFirstInvalidField: (formSelector?: string) => Promise<void>;
}

/**
 * Custom hook for handling form validation error scrolling and focus logic.
 * @param options - Configuration options for scroll behavior and delay
 * @returns Validation error handlers and scroll utilities
 */
export const useFormValidation = (
  options: UseFormValidationOptions = {}
): UseFormValidationReturn => {
  const { scrollOffset = 180, scrollBehavior = "smooth", scrollDelay = 200 } = options;

  /**
   * Handles validation errors by scrolling to the first invalid field or error element.
   * @param formSelector - CSS selector for the form container
   * @param errorSelector - CSS selector for error elements
   * @param skipScroll - If true, skip scrolling
   */
  const handleValidationError = useCallback(
    async (
      formSelector?: string,
      errorSelector?: string,
      skipScroll: boolean = false
    ): Promise<void> => {
      if (skipScroll) {
        return;
      }

      // Add a delay to ensure DOM updates are complete
      await new Promise(resolve => setTimeout(resolve, scrollDelay));

      try {
        if (formSelector) {
          await scrollToFirstInvalidField(formSelector, scrollOffset, scrollBehavior);
        } else if (errorSelector) {
          await scrollToValidationError(errorSelector, scrollOffset, scrollBehavior);
        } else {
          // Default behavior - look for validation errors
          await scrollToValidationError(
            ".validation-error",
            scrollOffset,
            scrollBehavior
          );
        }
      } catch (error) {
        console.error("Error scrolling to validation error:", error);
      }
    },
    [scrollOffset, scrollBehavior, scrollDelay]
  );

  return {
    handleValidationError,
    scrollToValidationError: async (errorSelector?: string) =>
      scrollToValidationError(
        errorSelector || ".validation-error",
        scrollOffset,
        scrollBehavior
      ),
    scrollToFirstInvalidField: async (formSelector?: string) =>
      scrollToFirstInvalidField(formSelector || "form", scrollOffset, scrollBehavior)
  };
};
